import { type Href, usePathname, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import {
  Image,
  type ImageProps,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";

import {
  Button,
  Icon,
  ThemedLoader,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { BurgerToggle } from "@/components/BurgerToggle";
import {
  SchoolBurgerMenu,
  type SchoolNavigationItem,
} from "@/components/SchoolBurgerMenu";

type SchoolAuthenticatedHeaderProps = {
  logo?: ImageProps["source"];
  navigationItems?: readonly SchoolNavigationItem[];
  currentSchool?: {
    name: string;
    abbreviation?: string;
  } | null;
  currentSchoolLoading?: boolean;
  onLogout?: () => void;
  logoutLabel?: string;
  showNavigation?: boolean;
  burgerBreakpoint?: number;
  schoolInPanelBreakpoint?: number;
};

export const SchoolAuthenticatedHeader = ({
  logo,
  navigationItems = [],
  currentSchool,
  currentSchoolLoading,
  onLogout,
  logoutLabel = "Abmelden",
  showNavigation = true,
  burgerBreakpoint = 1400,
  schoolInPanelBreakpoint = 768,
}: SchoolAuthenticatedHeaderProps) => {
  const { colors, fonts } = useCustomTheme();
  const { width: viewportWidth } = useWindowDimensions();
  const router = useRouter();
  const pathname = usePathname();

  const [isHovered, setIsHovered] = useState(false);
  const [hoveredNavigationKey, setHoveredNavigationKey] = useState<
    string | null
  >(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const showCollapsedMenu = showNavigation && viewportWidth < burgerBreakpoint;
  const showSchoolInPanel =
    showCollapsedMenu && viewportWidth < schoolInPanelBreakpoint;

  useEffect(() => {
    if (!showCollapsedMenu) {
      setMenuOpen(false);
    }
  }, [showCollapsedMenu]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const handleNavigation = (href: Href) => {
    router.push(href);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.logoContainer}>
        {logo && (
          <Image source={logo} style={styles.logo} resizeMode="contain" />
        )}
      </View>

      {showNavigation && !showCollapsedMenu && (
        <View style={styles.navigationContainer}>
          {navigationItems.map((item) => {
            const itemOpacity =
              item.isActive || hoveredNavigationKey === item.key ? 1 : 0;

            return (
              <Pressable
                key={item.key}
                accessibilityRole="link"
                style={styles.navigationItem}
                onPress={() => handleNavigation(item.href)}
                onHoverIn={() => {
                  if (!item.isActive) {
                    setHoveredNavigationKey(item.key);
                  }
                }}
                onHoverOut={() => {
                  setHoveredNavigationKey((current) =>
                    current === item.key ? null : current,
                  );
                }}
              >
                <ThemedText
                  style={[
                    styles.navigationText,
                    {
                      color: item.isActive ? colors.border : colors.text,
                      ...fonts.regular,
                    },
                  ]}
                >
                  {item.label}
                </ThemedText>

                <View
                  style={[
                    styles.activeIndicator,
                    Platform.OS === "web"
                      ? ({ transitionProperty: "opacity" } as any)
                      : undefined,
                    Platform.OS === "web"
                      ? ({ transitionDuration: "180ms" } as any)
                      : undefined,
                    Platform.OS === "web"
                      ? ({ transitionTimingFunction: "ease-in-out" } as any)
                      : undefined,
                    {
                      backgroundColor: colors.border,
                      opacity: itemOpacity,
                    },
                  ]}
                />
              </Pressable>
            );
          })}
        </View>
      )}

      {(!showNavigation || showCollapsedMenu) && <View style={styles.spacer} />}

      <View
        style={[styles.rightSection, { backgroundColor: colors.background }]}
      >
        {!showSchoolInPanel && (currentSchool || currentSchoolLoading) && (
          <View style={styles.schoolContainer}>
            <Icon
              iconSet="material-community"
              icon="school-outline"
              size={22}
              color={colors.border}
              cssStyle={styles.schoolIcon}
            />
            {currentSchool ? (
              <ThemedText
                numberOfLines={2}
                style={[
                  styles.schoolText,
                  { color: colors.semiMuted, ...fonts.semiBold },
                ]}
              >
                {currentSchool.name}
                {currentSchool.abbreviation &&
                  ` (${currentSchool.abbreviation})`}
              </ThemedText>
            ) : currentSchoolLoading ? (
              <ThemedLoader spinnerSize={30} />
            ) : null}
          </View>
        )}

        {showCollapsedMenu ? (
          <BurgerToggle
            isOpen={menuOpen}
            onToggle={() => setMenuOpen((v) => !v)}
          />
        ) : onLogout ? (
          <Button
            buttonText={logoutLabel}
            onPress={onLogout}
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
            iconName="logout"
            iconPosition="left"
            iconColor={colors.background}
            iconSize={22}
            buttonStyle={[
              styles.logoutButton,
              {
                backgroundColor: isHovered
                  ? colors.palette.spinach20
                  : colors.border,
              },
            ]}
            buttonTextStyle={[
              styles.logoutButtonText,
              fonts.bold,
              { color: colors.background },
            ]}
          />
        ) : null}
      </View>

      <SchoolBurgerMenu
        isOpen={showCollapsedMenu && menuOpen}
        navigationItems={navigationItems}
        currentSchool={currentSchool}
        currentSchoolLoading={currentSchoolLoading}
        showSchoolInPanel={showSchoolInPanel}
        logoutLabel={logoutLabel}
        onClose={() => setMenuOpen(false)}
        onNavigate={handleNavigation}
        onLogout={() => {
          onLogout?.();
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 40,
    boxShadow: "0px 8px 16px 0px #0000001A",
    elevation: 3,
    zIndex: 1000,
    height: 84,
    position: "relative",
  },
  logoContainer: {
    marginRight: 56,
  },
  logo: {
    width: 201,
    height: 50,
  },
  navigationContainer: {
    flexDirection: "row",
    flex: 1,
    gap: 32,
    height: "100%",
  },
  navigationItem: {
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  navigationText: {
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  activeIndicator: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
  },
  spacer: {
    flex: 1,
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    height: "100%",
    paddingRight: 40,
  },
  schoolContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 0,
    paddingVertical: 8,
    width: 200,
    justifyContent: "flex-start",
  },
  schoolIcon: {
    marginRight: 8,
  },
  schoolText: {
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 12 * 0.02,
  },
  logoutButton: {
    height: 44,
    paddingHorizontal: 16,
    borderRadius: 22,
    gap: 0,
  },
  logoutButtonText: {
    fontSize: 16,
    lineHeight: 20.8,
    paddingHorizontal: 8,
  },
});
